import { Box, Button, CircularProgress, Typography } from '@mui/material'
import { FC, ReactNode, isValidElement } from 'react'
import { tokens } from '@/theme'

export type EstadoDeListaTipo = 'carregando' | 'vazio' | 'erro' | 'semPermissao' | 'offline'

const MENSAGEM_PADRAO: Record<EstadoDeListaTipo, string> = {
  carregando: 'Carregando...',
  vazio: 'Nada por aqui ainda',
  erro: 'Não foi possível carregar os dados',
  semPermissao: 'Você não tem permissão para ver este conteúdo',
  offline: 'Sem conexão — verifique sua internet e tente novamente',
}

export interface EstadoDeListaProps {
  estado: EstadoDeListaTipo
  /** Substitui a mensagem padrão do estado. */
  mensagem?: string
  /** Linha de apoio abaixo da mensagem. */
  detalhe?: string
  /** Ícone acima da mensagem (não se aplica a `carregando`, que mostra o spinner). */
  icone?: ReactNode
  /** Botão de ação ("Tentar novamente", "Nova reserva") ou um nó pronto. */
  acao?: { label: string; onClick: () => void } | ReactNode
  /** Texto claro, para uso sobre fundo escuro (overlay do LoadingSpinner). */
  inverso?: boolean
  minHeight?: number | string
}

/**
 * Estado não-feliz de qualquer lista/tabela (Onda 0, item 4): um único componente para
 * carregando/vazio/erro/sem permissão/offline, para todas as telas contarem a mesma
 * história. `EmptyState` e `LoadingSpinner` viraram wrappers dele.
 */
export const EstadoDeLista: FC<EstadoDeListaProps> = ({
  estado,
  mensagem,
  detalhe,
  icone,
  acao,
  inverso = false,
  minHeight = 240,
}) => {
  const texto = mensagem ?? MENSAGEM_PADRAO[estado]
  const corTitulo = inverso ? '#FFFFFF' : tokens.brandInk
  const corDetalhe = inverso ? 'rgba(255,255,255,0.72)' : tokens.textSecondary

  const botao =
    acao && !isValidElement(acao) && typeof acao === 'object' && 'label' in acao ? (
      <Button variant="contained" color="primary" onClick={acao.onClick}>
        {acao.label}
      </Button>
    ) : (
      (acao as ReactNode) ?? null
    )

  return (
    <Box
      role={estado === 'erro' ? 'alert' : undefined}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        padding: 4,
        textAlign: 'center',
        minHeight,
      }}
    >
      {estado === 'carregando' ? (
        <CircularProgress size={36} sx={{ color: tokens.brandPrimary, mb: 1 }} />
      ) : (
        icone && (
          <Box sx={{ fontSize: '3rem', color: '#BDC3C7', mb: 1, display: 'flex' }}>{icone}</Box>
        )
      )}

      <Typography sx={{ fontWeight: 600, color: corTitulo, letterSpacing: '-0.02em' }}>
        {texto}
      </Typography>

      {detalhe && (
        <Typography variant="body2" sx={{ color: corDetalhe, maxWidth: 340 }}>
          {detalhe}
        </Typography>
      )}

      {estado !== 'carregando' && botao && <Box sx={{ mt: 1.5 }}>{botao}</Box>}
    </Box>
  )
}

export default EstadoDeLista
